import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  RiCloseLine,
  RiDownloadLine,
  RiFileImageLine,
  RiFilePdfLine,
  RiImageAddLine,
  RiRefreshLine,
  RiSaveLine,
  RiUploadCloud2Line,
} from 'react-icons/ri';

import { EditorContext } from '../../../../../imageEditorComponent';
import { isFreeFormBannerMode } from '../../../../bannerFrameTypes';
import { useStore } from '../../hooks';
import TopbarIconButton from './TopbarIconButton';

const SAVE_MENU_WIDTH = 300;
const SAVE_MENU_GAP = 8;
const SAVE_MENU_VIEWPORT_PAD = 12;

const isMacPlatform =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);

const SAVE_MENU_SHORTCUT = isMacPlatform ? '⌘ S' : 'Ctrl S';

/** Platform / admin tenant — hide Submit in standalone; dashboard iframe still needs it. */
const isPlatformTenantId = (tenantId) =>
  tenantId === 0 || tenantId === '0' || tenantId === '' || tenantId == null;

/** True when the editor is running inside a parent frame (dashboard embed). */
const isEmbeddedInParentFrame = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    if (window.self !== window.top) {
      return true;
    }
  } catch {
    // Cross-origin access to window.top can throw; treat as embedded.
    return true;
  }
  return Boolean(window.location.ancestorOrigins?.length);
};

function SaveMenuItem({ icon: Icon, label, hint, onClick }) {
  return (
    <button type="button" className="respark_topbar-save-menu-item" onClick={onClick}>
      <span className="respark_topbar-save-menu-item-icon" aria-hidden>
        <Icon size={18} />
      </span>
      <span className="respark_topbar-save-menu-item-body">
        <span className="respark_topbar-save-menu-item-label">{label}</span>
        {hint ? <span className="respark_topbar-save-menu-item-hint">{hint}</span> : null}
      </span>
    </button>
  );
}

function SaveMenuSection({ title, children }) {
  if (!children) {
    return null;
  }

  return (
    <div className="respark_topbar-save-menu-section">
      {title ? (
        <div className="respark_topbar-save-menu-section-label">{title}</div>
      ) : null}
      <div className="respark_topbar-save-menu-section-items">{children}</div>
    </div>
  );
}

function computeSaveMenuPosition(anchorEl) {
  if (!anchorEl) {
    return null;
  }

  const rect = anchorEl.getBoundingClientRect();
  const maxWidth = Math.min(
    SAVE_MENU_WIDTH,
    window.innerWidth - SAVE_MENU_VIEWPORT_PAD * 2,
  );
  let top = rect.bottom + SAVE_MENU_GAP;
  let left = rect.right - maxWidth;

  left = Math.max(
    SAVE_MENU_VIEWPORT_PAD,
    Math.min(left, window.innerWidth - maxWidth - SAVE_MENU_VIEWPORT_PAD),
  );

  const maxHeight = window.innerHeight - top - SAVE_MENU_VIEWPORT_PAD;
  if (maxHeight < 120) {
    top = Math.max(
      SAVE_MENU_VIEWPORT_PAD,
      rect.top - SAVE_MENU_GAP - Math.min(360, window.innerHeight - SAVE_MENU_VIEWPORT_PAD * 2),
    );
  }

  return {
    top,
    left,
    width: maxWidth,
    maxHeight: Math.max(120, window.innerHeight - top - SAVE_MENU_VIEWPORT_PAD),
  };
}

function SaveButtonCustom() {
  const { setCurrentAction, editorConfigContext, activeTemplateContext } =
    useContext(EditorContext);
  const {
    t,
    config: { keyboardShortcutHandlersRef } = {},
  } = useStore();
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);
  const wrapRef = useRef(null);
  const panelRef = useRef(null);

  const isFreeFormMode = isFreeFormBannerMode(editorConfigContext?.type);
  const isEmbedded = isEmbeddedInParentFrame();
  const showUpdate =
    !isFreeFormMode &&
    (isPlatformTenantId(editorConfigContext?.tenantId)
      ? true
      : !isPlatformTenantId(activeTemplateContext?.tenantId));
  // Submit returns the banner to the parent (dashboard) via postMessage.
  // Show whenever embedded — including tenant 0 — and for non-platform tenants in standalone.
  const showSubmit =
    !isFreeFormMode && (isEmbedded || !isPlatformTenantId(editorConfigContext?.tenantId));
  const showTemplates = !isFreeFormMode;

  const closeMenu = useCallback(() => {
    setOpen(false);
  }, []);

  const openMenu = useCallback(() => {
    setOpen(true);
  }, []);

  useEffect(() => {
    const handlersRef = keyboardShortcutHandlersRef;
    if (!handlersRef || typeof handlersRef !== 'object' || !('current' in handlersRef)) {
      return undefined;
    }

    if (!handlersRef.current) {
      handlersRef.current = {};
    }
    handlersRef.current.openSaveMenu = openMenu;

    return () => {
      if (handlersRef.current) {
        delete handlersRef.current.openSaveMenu;
      }
    };
  }, [keyboardShortcutHandlersRef, openMenu]);

  const handleSaveClick = useCallback(
    (action) => {
      if (!action) {
        return;
      }
      setCurrentAction({ type: 'save', action });
      closeMenu();
    },
    [closeMenu, setCurrentAction],
  );

  const updatePanelPosition = useCallback(() => {
    if (!wrapRef.current) {
      return;
    }
    setPanelStyle(computeSaveMenuPosition(wrapRef.current));
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      return undefined;
    }

    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);

    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [open, updatePanelPosition]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onPointerDown = (event) => {
      const target = event.target;
      if (
        wrapRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      closeMenu();
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [closeMenu, open]);

  const templateItems = showTemplates ? (
    <>
      <SaveMenuItem
        icon={RiImageAddLine}
        label="Save as new template"
        onClick={() => handleSaveClick('add')}
      />
      {showUpdate ? (
        <SaveMenuItem
          icon={RiRefreshLine}
          label="Update selected template"
          onClick={() => handleSaveClick('update')}
        />
      ) : null}
    </>
  ) : null;

  const publishItems = showSubmit ? (
    <SaveMenuItem
      icon={RiUploadCloud2Line}
      label="Submit banner image"
      onClick={() => handleSaveClick('submit')}
    />
  ) : null;

  const downloadItems = (
    <>
      <SaveMenuItem
        icon={RiDownloadLine}
        label="Download JPEG"
        onClick={() => handleSaveClick('download-jpeg')}
      />
      <SaveMenuItem
        icon={RiDownloadLine}
        label="Download PNG"
        onClick={() => handleSaveClick('download-png')}
      />
      <SaveMenuItem
        icon={RiFileImageLine}
        label="Download PNG (transparent)"
        hint={t('saveDownloadPngTransparentHint')}
        onClick={() => handleSaveClick('download-png-transparent')}
      />
      <SaveMenuItem
        icon={RiFilePdfLine}
        label="Download PDF"
        hint={t('saveDownloadPdfHint')}
        onClick={() => handleSaveClick('download-pdf')}
      />
    </>
  );

  const showPublishSection = showSubmit;
  const showDividerBeforeDownload = showTemplates || showPublishSection;

  const panel =
    open &&
    panelStyle &&
    createPortal(
      <div
        ref={panelRef}
        className="respark_topbar-save-menu"
        role="dialog"
        aria-modal="false"
        aria-label="Save and export"
        style={panelStyle}
      >
        <div className="respark_topbar-save-menu-header">
          <h2 className="respark_topbar-save-menu-title">Save &amp; export</h2>
          <button
            type="button"
            className="respark_topbar-save-menu-close"
            title="Close save menu"
            aria-label="Close save menu"
            onClick={closeMenu}
          >
            <RiCloseLine size={18} aria-hidden />
          </button>
        </div>
        <div className="respark_topbar-save-menu-body" role="menu">
          {showTemplates ? (
            <SaveMenuSection title="Templates">{templateItems}</SaveMenuSection>
          ) : null}
          {showPublishSection ? (
            <>
              <div className="respark_topbar-save-menu-divider" aria-hidden />
              <SaveMenuSection title="Publish">{publishItems}</SaveMenuSection>
            </>
          ) : null}
          {showDividerBeforeDownload ? (
            <div className="respark_topbar-save-menu-divider" aria-hidden />
          ) : null}
          <SaveMenuSection title="Download">{downloadItems}</SaveMenuSection>
        </div>
      </div>,
      document.body,
    );

  return (
    <div ref={wrapRef} className="respark_topbar-save-wrap">
      <TopbarIconButton
        className={open ? 'respark_topbar-btn--on' : ''}
        title={`Save & export (${SAVE_MENU_SHORTCUT})`}
        ariaLabel={`Save and export, ${SAVE_MENU_SHORTCUT}`}
        ariaPressed={open}
        onClick={() => setOpen((current) => !current)}
      >
        <RiSaveLine size={18} aria-hidden />
      </TopbarIconButton>
      {panel}
    </div>
  );
}

export default SaveButtonCustom;
